import React, { ForwardedRef, forwardRef, useEffect, useMemo, useState } from "react";
import { Backdrop, Grow, Paper, Popper } from "@mui/material";
import { PropsWithChildren } from "react";
import { useAppSettings } from "../../contexts/app-context/AppContext";
import { createTheme, ThemeProvider } from '@mui/material/styles';

export type BreadCrumbMenuProps = {
    open: boolean;
    anchorEl?: HTMLElement | null;
    placement?: "bottom" | "right";
    offset?: [number, number];
    onClose?: () => void;
};

function BreadCrumbMenu({
    open,
    anchorEl,
    placement,
    offset,
    onClose,
    children
}: PropsWithChildren<BreadCrumbMenuProps>, ref?: ForwardedRef<HTMLDivElement>) {
    const [appSettings] = useAppSettings();
    const appHeaderTheme = useMemo(
        () => {
            return createTheme(appSettings.themeSet[appSettings.themeSet.mode].main);
        },
        [appSettings.themeSet]
    );

    function getAnchorRect() {
        const rect = anchorEl?.getBoundingClientRect();
        return rect
            ? {
                top: rect.top + window.scrollY,
                left: rect.left + window.scrollX,
                width: rect.width,
                height: rect.height
            }
            : {
                top: 0,
                left: 0,
                width: 0,
                height: 0
            };
    }

    const anchorRect = getAnchorRect();
    const maxHeight = placement === "right"
        ? `calc(100vh - ${anchorRect.top + 16}px)`
        : `calc(100vh - ${anchorRect.top + anchorRect.height + 16}px)`;

    return (
        <ThemeProvider theme={appHeaderTheme}>
            <Backdrop
                open={open}
                onClick={
                    () => {
                        onClose?.();
                    }
                }
                invisible
                sx={{
                    zIndex: (theme) => theme.zIndex.modal - 1
                }}
            />
            <Popper
                color='textSecondary'
                anchorEl={anchorEl}
                placement={placement === "right"
                    ? "right-start"
                    : "bottom-start"}
                modifiers={[{
                    name: 'offset',
                    options: {
                        offset: offset ?? [0, 0],
                    }
                }]}
                sx={{
                    display: "flex",
                    overflow: "hidden",
                    zIndex: (theme) => theme.zIndex.modal,
                    // Set maxHeight of popper based on available space below the anchor element.
                    // This will allow it to grow as needed but prevent it from going off screen.
                    maxHeight: maxHeight,
                    paddingTop: 1,
                    paddingRight: 1,
                    paddingBottom: 2,
                    paddingLeft: 2
                }}
                keepMounted
                open={open}
                transition
            >
                {({ TransitionProps }) => (
                    <Grow
                        {...TransitionProps}
                        timeout={350}
                        style={{
                            transformOrigin: "top"
                        }}
                    >
                        <Paper
                            variant="elevation"
                            elevation={8}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden",
                                flex: "1 1 auto"
                            }}
                            ref={ref}
                        >
                            {children}
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </ThemeProvider>
    );
}

export default forwardRef(BreadCrumbMenu);